import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardKpis,
} from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";
import TvBayTile from "./TvBayTile";
import TvOverlayShell, { TvDueNextStrip } from "./TvOverlayShell";

interface TvModeOverlayProps {
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  kpis?: IProductionDashboardKpis;
  now: DateTime;
  busyVehicleIds?: Set<string>;
  completedTodayCount: number;
  triageCount: number;
  depollutionCount: number;
  hideNonAssembly?: boolean;
  onClose: () => void;
}

// The Bays view blown up for a screen on the wall: one tile per bay inside the shared TV shell.
export default function TvModeOverlay({
  bays,
  assemblies,
  kpis,
  now,
  busyVehicleIds,
  completedTodayCount,
  triageCount,
  depollutionCount,
  hideNonAssembly = false,
  onClose,
}: TvModeOverlayProps) {
  const { t } = useTranslation("production");

  return (
    <TvOverlayShell
      now={now}
      onClose={onClose}
      headerContent={
        <>
          {kpis && (
            <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
              <span className="text-4xl font-semibold leading-none tabular-nums">
                {kpis.baysInUse.used}/{kpis.baysInUse.total}
              </span>
              <span className="text-base text-gray-400">{t("bays in use")}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
            <span className="text-4xl font-semibold leading-none tabular-nums">
              {completedTodayCount}
            </span>
            <span className="text-base text-gray-400">{t("dismantled today")}</span>
          </div>
          {kpis && (
            <div className="flex items-baseline gap-2 rounded-xl border border-gray-800 bg-gray-900 px-5 py-3">
              <span className="text-4xl font-semibold leading-none tabular-nums">
                {kpis.partsRemoved.total}
              </span>
              <span className="text-base text-gray-400">{t("parts off")}</span>
            </div>
          )}
        </>
      }
      footer={
        <TvDueNextStrip
          bays={bays}
          trailing={`${t("Triage")} ${triageCount} · ${t("Depollution")} ${depollutionCount}`}
        />
      }
    >
      {bays.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-2xl text-gray-400">
          {t("No dismantling bays configured.")}
        </div>
      ) : (
        <div
          className={classNames(
            "flex-1 min-h-0 grid auto-rows-fr gap-5",
            bays.length <= 4
              ? "grid-cols-2"
              : bays.length <= 8
                ? "grid-cols-4"
                : "grid-cols-5"
          )}
        >
          {bays.map((bay) => (
            <TvBayTile
              key={bay.id}
              bay={bay}
              assemblies={assemblies}
              now={now}
              busyVehicleIds={busyVehicleIds}
              hideNonAssembly={hideNonAssembly}
            />
          ))}
        </div>
      )}
    </TvOverlayShell>
  );
}
